/**
 * Consent and Google tag behaviour.
 *
 * What this decides, in plain terms:
 *
 *  - Visitors in the EEA, the UK or Switzerland get NO analytics or advertising storage until they say yes
 *    (opt-in). Everyone sees the same site; only the Google tag's behaviour differs.
 *  - Everywhere else, analytics and Google Ads CONVERSION MEASUREMENT run by default (opt-out), unless the
 *    visitor's browser sends Global Privacy Control, which turns the advertising parts off.
 *  - Ad personalisation (remarketing, audience building) is OFF for everyone unless the owner sets
 *    NEXT_PUBLIC_GA_ALLOW_REMARKETING=true. The site does not need it: the goal is to measure which campaigns
 *    produce enquiries, not to follow people around the web.
 *  - A visitor can change their mind at any time with "Privacy choices" in the footer.
 *
 * Before consent, where it is required, the Google tag is NOT EVEN LOADED or configured: no request goes to
 * Google and no Google cookie is set (Google's "basic" consent mode, the stricter of the two). It is configured
 * and loaded the moment the visitor accepts, after the consent update, so the first page view carries the
 * visitor's real choice. If they later decline, Google's own opt-out flag (`ga-disable-<id>`) switches it off.
 *
 * Where the visitor is comes from, in order of trust: the server's geolocation header (/api/region), the
 * browser's time zone as an immediate fallback, and Google's own `region` setting as a third layer. Any
 * doubt resolves to the stricter answer.
 *
 * The inline bootstrap in layout.tsx is built from `consentBootstrap()` below so it cannot drift from the
 * rules here; tests/consent.test.mjs runs both against the same table of cases.
 */

export const CONSENT_KEY = 'kit_consent';
export const CONSENT_EVENT = 'kit-consent-change';
export const OPEN_PREFERENCES_EVENT = 'kit-open-privacy-choices';
/** Ask again after a year. */
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

/** EU 27, Iceland, Liechtenstein, Norway (EEA), the United Kingdom and Switzerland. */
export const CONSENT_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT',
  'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
] as const;

export type ConsentValue = 'granted' | 'denied';
export type GoogleConsent = {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
};

/** What the visitor chose. `ads` covers Google Ads conversion measurement; `analytics` covers Google Analytics. */
export type Choice = { v: 1; analytics: boolean; ads: boolean; at: number };

/**
 * Time zones that suggest the visitor may be in the EEA, UK or Switzerland. Deliberately over-inclusive (all of
 * Europe/, plus the Atlantic islands that belong to EEA countries): wrongly asking someone for consent costs
 * one click; wrongly not asking is the error that matters.
 */
export function timezoneNeedsConsent(tz: string | undefined | null): boolean {
  if (!tz) return false;
  return /^Europe\//.test(tz) || /^(Atlantic\/(Reykjavik|Canary|Madeira|Azores|Faroe)|Arctic\/Longyearbyen)$/.test(tz);
}

export function countryNeedsConsent(code: string | undefined | null): boolean {
  return !!code && (CONSENT_COUNTRIES as readonly string[]).includes(code.toUpperCase());
}

export function validChoice(raw: unknown, now = Date.now()): Choice | null {
  const c = raw as Partial<Choice> | null;
  if (!c || c.v !== 1 || typeof c.analytics !== 'boolean' || typeof c.ads !== 'boolean' || typeof c.at !== 'number') return null;
  return now - c.at < CONSENT_TTL_MS && c.at <= now + 60_000 ? (c as Choice) : null;
}

/**
 * The Google consent state for a visitor. The ONLY place the rules live in TypeScript.
 *
 * `allowRemarketing` is the owner's switch; even then personalisation needs the visitor's advertising consent
 * and no Global Privacy Control signal.
 */
export function googleConsentFor(input: {
  needsConsent: boolean;
  gpc: boolean;
  choice: Choice | null;
  allowRemarketing?: boolean;
}): GoogleConsent {
  const { needsConsent, gpc, choice, allowRemarketing = false } = input;
  const adsAllowed = (choice ? choice.ads : !needsConsent) && !gpc;
  const analyticsAllowed = choice ? choice.analytics : !needsConsent;
  return {
    analytics_storage: analyticsAllowed ? 'granted' : 'denied',
    ad_storage: adsAllowed ? 'granted' : 'denied',
    ad_user_data: adsAllowed ? 'granted' : 'denied',
    ad_personalization: adsAllowed && allowRemarketing ? 'granted' : 'denied',
  };
}

/** Whether a Google Ads click id may be stored by us (the same condition as advertising storage). */
export function mayStoreAdClickId(consent: GoogleConsent): boolean {
  return consent.ad_storage === 'granted';
}

// ---- browser side -------------------------------------------------------------------------------------------

type Gtag = (...args: unknown[]) => void;

export function readChoice(): Choice | null {
  try {
    return validChoice(JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null'));
  } catch {
    return null;
  }
}

export function gpcOn(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

export const ALLOW_REMARKETING = process.env.NEXT_PUBLIC_GA_ALLOW_REMARKETING === 'true';

/** The two Google Analytics 4 properties this site reports to. layout.tsx passes the same list to the bootstrap. */
export const GA_IDS = ['G-R27V7YN8FL', 'G-CRJTBLZB3F'];

/** The state Google should currently be told, from storage, the browser's signals and the best location guess. */
export function currentGoogleConsent(needsConsent: boolean): GoogleConsent {
  return googleConsentFor({ needsConsent, gpc: gpcOn(), choice: readChoice(), allowRemarketing: ALLOW_REMARKETING });
}

/** Removes a cookie wherever Google may have set it: this host and each parent domain. */
function expireCookie(name: string) {
  const parts = location.hostname.split('.');
  const domains = ['', ...parts.map((_, i) => '.' + parts.slice(i).join('.')).filter((d) => d.split('.').length > 2 || parts.length === 1)];
  for (const d of new Set(domains)) {
    document.cookie = `${name}=; Max-Age=0; Path=/${d ? `; Domain=${d}` : ''}; SameSite=Lax`;
  }
}

/** Google's analytics and advertising cookies and stored keys, removed when the visitor withdraws consent. */
function clearGoogleStorage(analytics: boolean, ads: boolean) {
  try {
    const names = document.cookie.split('; ').map((c) => c.split('=')[0]);
    for (const n of names) {
      if (analytics && /^(_ga|_ga_[A-Z0-9]+|_gid|_gat.*)$/.test(n)) expireCookie(n);
      if (ads && /^(_gcl_(au|aw|dc|gb|gf|ha)|IDE|test_cookie)$/.test(n)) expireCookie(n);
    }
    if (ads) localStorage.removeItem('_gcl_ls');
    if (analytics) {
      // Our own attribution records are analytics storage too: the first landing page, the last page and the entry button.
      localStorage.removeItem('kit_first_touch');
      sessionStorage.removeItem('kit_prev_page');
      sessionStorage.removeItem('kit_entry_cta');
    }
  } catch {
    /* ignore */
  }
}

export function pushConsentUpdate(consent: GoogleConsent) {
  try {
    (window as unknown as { __kitConsent?: GoogleConsent }).__kitConsent = consent;
    for (const id of GA_IDS) (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = consent.analytics_storage !== 'granted';
    // Withdrawing consent also removes what we hold: the first-party visitor cookie and any stored ad click id.
    if (consent.analytics_storage !== 'granted') document.cookie = 'kit_vid=; Max-Age=0; Path=/; SameSite=Lax';
    if (consent.ad_storage !== 'granted') localStorage.removeItem('kit_ad_click');
    clearGoogleStorage(consent.analytics_storage !== 'granted', consent.ad_storage !== 'granted');
    const gtag = (window as unknown as { gtag?: Gtag }).gtag;
    if (typeof gtag === 'function') gtag('consent', 'update', consent);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));
  } catch {
    /* never affects the visitor */
  }
}

export function saveChoice(analytics: boolean, ads: boolean): Choice {
  const choice: Choice = { v: 1, analytics, ads, at: Date.now() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choice));
  } catch {
    /* storage blocked: the choice still applies for this page view */
  }
  return choice;
}

export function clearChoice() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
}

// ---- the inline bootstrap -----------------------------------------------------------------------------------

/** Query parameters that may appear in the page address sent to Google. Everything else is dropped. */
export const URL_ALLOWLIST = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'gad_source',
  'gad_campaignid', 'service', 'tier',
] as const;

/**
 * The script that runs BEFORE the Google tag configures anything: sets Consent Mode defaults, then configures
 * each property with a sanitised page address and with signals and personalisation off. Returned as a string
 * for an inline <script>. No personal data can enter it: it only reads the address, the referrer, one
 * localStorage key, the time zone and the Global Privacy Control flag.
 */
export function consentBootstrap(opts: { ids: string[]; allowRemarketing: boolean }): string {
  const cfg = { ids: opts.ids, regions: CONSENT_COUNTRIES, keep: URL_ALLOWLIST, key: CONSENT_KEY, ttl: CONSENT_TTL_MS, remarketing: opts.allowRemarketing };
  return `(function(C){
window.dataLayer=window.dataLayer||[];
window.gtag=function(){dataLayer.push(arguments);};
gtag('js',new Date());
var ids=C.ids;
function load(){if(window.__kitGtag)return;window.__kitGtag=1;var go=function(){var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id='+encodeURIComponent(ids[0]);document.head.appendChild(s);};var idle=function(){if(window.requestIdleCallback)window.requestIdleCallback(go,{timeout:2000});else setTimeout(go,200);};if(document.readyState==='complete')idle();else window.addEventListener('load',idle,{once:true});}
function disable(on){ids.forEach(function(id){window['ga-disable-'+id]=on;});}
function needsTz(tz){return !!tz&&(/^Europe\\//.test(tz)||/^(Atlantic\\/(Reykjavik|Canary|Madeira|Azores|Faroe)|Arctic\\/Longyearbyen)$/.test(tz));}
var tz='';try{tz=Intl.DateTimeFormat().resolvedOptions().timeZone||'';}catch(e){}
var gpc=false;try{gpc=navigator.globalPrivacyControl===true;}catch(e){}
var choice=null;try{var r=JSON.parse(localStorage.getItem(C.key)||'null');var n=Date.now();if(r&&r.v===1&&typeof r.analytics==='boolean'&&typeof r.ads==='boolean'&&typeof r.at==='number'&&n-r.at<C.ttl&&r.at<=n+60000)choice=r;}catch(e){}
var needs=needsTz(tz);
var ads=(choice?choice.ads:!needs)&&!gpc;
var an=choice?choice.analytics:!needs;
var g=function(v){return v?'granted':'denied';};
window.__kitConsent={analytics_storage:g(an),ad_storage:g(ads),ad_user_data:g(ads),ad_personalization:g(ads&&C.remarketing)};
gtag('consent','default',{analytics_storage:g(an),ad_storage:g(ads),ad_user_data:g(ads),ad_personalization:g(ads&&C.remarketing),wait_for_update:500});
gtag('consent','default',{analytics_storage:'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',region:C.regions});
function clean(u,full){try{var x=new URL(u);if(!full)return x.origin+x.pathname;var k=new URLSearchParams();x.searchParams.forEach(function(v,n){if(C.keep.indexOf(n)>-1)k.set(n,String(v).slice(0,100));});x.search=k.toString();x.hash='';return x.toString();}catch(e){return u;}}
var extra=C.remarketing?{}:{allow_google_signals:false,allow_ad_personalization_signals:false};
var ref=document.referrer?clean(document.referrer,false):'';
function configure(){if(window.__kitConfigured)return;window.__kitConfigured=1;C.ids.forEach(function(id){var o={page_location:clean(location.href,true)};if(ref)o.page_referrer=ref;for(var k in extra)o[k]=extra[k];gtag('config',id,o);});}
disable(!an);
if(an||ads){configure();load();}
window.addEventListener('kit-consent-change',function(e){var d=(e&&e.detail)||{};disable(d.analytics_storage!=='granted');if(d.analytics_storage==='granted'||d.ad_storage==='granted'){configure();load();}});
})(${JSON.stringify(cfg)});`;
}
